import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Hand, MousePointer2, ScanFace, ShieldCheck, SlidersHorizontal, Sparkles, ToggleRight } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { PhotoPersonTargets } from "@/components/workspace/RevealKit";
import FullscreenViewer from "@/components/workspace/FullscreenViewer";
import { LAYERS, type RevealLayer, type RevealProtect } from "@/lib/revealKit";

// Public demo of preview protection (photohawk.com/preview-protection), linked from the launch email.
// The photo is a stock marketing image (src/assets/for-participants-hero.jpg) run through the REAL
// renderer (photohawk-serverless lib/previewProtection: people outlined by the YOLO segmenter, covered,
// faces kept clear, reveal layers frame / top / bottom, default PREVIEW watermark) and saved as static
// files under public/images/demo/preview-protection/. The reveal uses the same components buyers get
// (PhotoPersonTargets + FullscreenViewer, ports of photohawk-app's), fed from those files.
const DIR = "/images/demo/preview-protection";
const DEMO = {
  width: 1200,
  height: 675,
  protect: {
    ready: true,
    reveal: "layers",
    bandHeight: 0.25,
    halves: { top: [0, 0.625], bottom: [0.375, 1] },
    regions: [
      { x: 24.19, y: 6.57, width: 38.76, height: 93.43, win: { x: 0, y: 0, width: 100, height: 100 } },
      { x: 20.32, y: 48.63, width: 8.16, height: 38.67, win: { x: 1.92, y: 45.48, width: 44.97, height: 44.97 } },
    ],
  } as RevealProtect,
};
const layerUrl = (i: number, layer: RevealLayer) => (LAYERS.includes(layer) ? `${DIR}/spot${i}-${layer}.jpg` : undefined);

const steps = [
  {
    icon: ScanFace,
    title: "People are softly covered",
    body: "Each person below the head is blurred inside their own outline, along with the bike they ride, because that's where race numbers live: softly in gallery thumbnails, more strongly when a photo is opened, and only lightly in close-ups. Faces are never blurred, so buyers still spot themselves while they scroll.",
  },
  {
    icon: Hand,
    title: "Tap a person to see them clearly",
    body: "A tap (or click) opens a full-screen viewer on that person. A clear band slides over them from head to toe, and they can drag the slider to check every detail before they buy.",
  },
  {
    icon: ShieldCheck,
    title: "A screenshot is no use to AI removers",
    body: "The watermark stays on throughout, and the screen only ever shows one band of the person clear. Watermark-removal tools need the whole picture underneath, so there's nothing for them to work with.",
  },
];

const PreviewProtection = () => {
  const [person, setPerson] = useState<number | null>(null);
  const close = useCallback(() => setPerson(null), []);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader variant="marketing" />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-hawk-navy text-white overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
              <Sparkles size={16} className="text-hawk-orange" />
              <span className="text-sm font-medium">New for photographers</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
              Previews that help buyers find themselves, <span className="text-gradient-primary">not copy your work</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl">
              Some buyers screenshot a watermarked preview and run it through a free AI watermark remover instead of buying.
              Preview protection keeps your previews just as useful for finding yourself, while making a screenshot worthless.
            </p>
          </div>
        </div>
      </section>

      {/* Live demo */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">
                Try it: <span className="text-hawk-orange">tap the runner</span>
              </h2>
              <p className="text-muted-foreground">
                This is exactly what a buyer sees in a protected gallery. Tap or click a person to open the full-screen viewer.
              </p>
            </div>
            <div
              className="relative mx-auto overflow-hidden rounded-2xl bg-black shadow-xl select-none"
              style={{ aspectRatio: `${DEMO.width} / ${DEMO.height}`, WebkitTouchCallout: "none" }}
              onContextMenu={(e) => e.preventDefault()}
            >
              <img
                src={`${DIR}/preview.jpg`}
                alt="A protected preview: the runner is softly blurred below the face, with a tap icon on him"
                draggable={false}
                className="absolute inset-0 h-full w-full"
                width={DEMO.width}
                height={DEMO.height}
              />
              <PhotoPersonTargets protect={DEMO.protect} layerUrl={layerUrl} onOpen={setPerson} />
            </div>
            <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Hand className="h-4 w-4" /> Phone: tap a person</span>
              <span className="inline-flex items-center gap-1.5"><MousePointer2 className="h-4 w-4" /> Computer: click a person</span>
              <span className="inline-flex items-center gap-1.5"><SlidersHorizontal className="h-4 w-4" /> Then drag the slider to scan</span>
            </div>
            <p className="mt-2 text-center text-xs text-muted-foreground">Demo image. In your galleries, people are detected automatically in every photo.</p>
          </div>
        </div>
      </section>

      {/* Before / now */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-10">
              What a screenshot <span className="text-hawk-orange">used to get</span>, and what it gets now
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <figure className="space-y-3">
                <img src={`${DIR}/before.jpg`} alt="Before: the whole photo, with only a watermark over it" className="w-full rounded-xl shadow-lg" loading="lazy" width={DEMO.width} height={DEMO.height} />
                <figcaption className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">Before:</span> a watermark over the full photo. AI removers can rebuild what's underneath.
                </figcaption>
              </figure>
              <figure className="space-y-3">
                <img src={`${DIR}/preview.jpg`} alt="Now: the same preview with people softly blurred and faces clear" className="w-full rounded-xl shadow-lg" loading="lazy" width={DEMO.width} height={DEMO.height} />
                <figcaption className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">Now:</span> people are softly blurred, faces stay clear. There's nothing underneath to recover.
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {steps.map((s) => (
              <div key={s.title} className="bg-card rounded-2xl p-7 shadow-lg">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-hawk-orange to-hawk-gold flex items-center justify-center mb-4">
                  <s.icon className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-display font-bold mb-2">{s.title}</h3>
                <p className="text-muted-foreground text-sm">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For photographers */}
      <section className="py-16 bg-hawk-navy text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <ToggleRight className="mx-auto mb-4 text-hawk-orange" size={40} />
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">On by default. Your call, gallery by gallery.</h2>
            <p className="text-white/80 mb-3">
              Preview protection is switched on for every gallery that sells photos. Free-download galleries aren't affected, and selfie and bib search work exactly as before.
            </p>
            <p className="text-white/80 mb-8">
              To turn it off for a gallery, open the gallery and go to <strong>Settings → Security watermark → Preview protection</strong>. The same setting shows an example made from your own photos.
            </p>
            <Link to="/admin/workspaces">
              <Button variant="hero" size="lg" className="group">
                Open your galleries
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {person !== null && (
        <FullscreenViewer
          src={`${DIR}/preview.jpg`}
          protect={DEMO.protect}
          layerUrl={layerUrl}
          alt="Preview protection demo"
          initialSubject={person}
          onClose={close}
        />
      )}
    </div>
  );
};

export default PreviewProtection;
